import { hasMergeIntent } from '../../../components/chat/fixProgress';
import { authHeaders } from '../../../api';
import { mapRemediationTasks } from './remediationMapper';

const API_BASE = import.meta.env.VITE_API_URL || 'https://api.ocnexus.dev';

interface SyncTasksResponse {
  tasks?: unknown[];
}

export class RemediationApiError extends Error {
  constructor(message: string, public readonly status: number, public readonly code?: string) {
    super(message);
    this.name = 'RemediationApiError';
  }
}

export function isFixAlreadyRunning(error: unknown): boolean {
  return error instanceof RemediationApiError && error.status === 409
    && (error.code === 'fix_already_running' || error.code === 'fix_pr_exists');
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const auth = await authHeaders();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...auth,
  };

  const response = await fetch(`${API_BASE}/api${path}`, {
    ...init,
    headers,
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: response.statusText }));
    throw new RemediationApiError(body.reason || body.error || body.message || `API error: ${response.status}`, response.status, body.code);
  }

  return response.json() as Promise<T>;
}

export async function fetchRemediationTasks(projectId: string) {
  const response = await request<SyncTasksResponse>(`/sync/tasks/${projectId}`);
  return mapRemediationTasks(response.tasks ?? []);
}

export async function executeRemediationTask(taskId: string) {
  return request<unknown>(`/sync/tasks/${taskId}/fix`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export interface MergeOpenFixResult {
  task_id: string;
  title: string;
  pr_number: number;
  merged: boolean;
  /** Why it was not merged. */
  note?: string;
}

/** Merge every open fix pull request on the project. The customer asking is the approval. */
export async function mergeOpenFixPullRequests(projectId: string, command: string): Promise<{ accepted: boolean; results: MergeOpenFixResult[] }> {
  if (!hasMergeIntent(command)) throw new Error('An explicit request to merge the open PRs is required.');
  const response = await request<{ accepted?: boolean; results?: MergeOpenFixResult[] }> (`/sync/tasks/${encodeURIComponent(projectId)}/merge-open`, {
    method: 'POST',
    body: JSON.stringify({ command }),
  });
  return { accepted: response.accepted === true, results: response.results ?? [] };
}
